<!-- Menú de una conexión: solo la quita (regla, orden y cambios de pregunta están en la hoja del campo). Solo emite:
     el padre la quita con deshacer. Esc o clic fuera cierra; Supr quita. -->
<template>
  <div ref="rootRef" class="em" role="dialog" :aria-labelledby="titleId" :style="boxStyle" @keydown="onKey">
    <div class="em-head">
      <h3 :id="titleId" ref="titleRef" class="em-title" tabindex="-1">
        «{{ label }}» <span class="em-arrow" aria-hidden="true">→</span> <span class="mono">{{ field }}</span>
      </h3>
      <button type="button" class="em-x" :aria-label="t('ed.close')" :title="t('ed.close')" @click="emit('close')">✕</button>
    </div>
    <div class="em-sub mono">{{ alias }}</div>
    <div class="em-foot">
      <button type="button" class="em-act danger" @click="emit('remove')">
        <span aria-hidden="true">⌫</span> {{ t('em.remove') }} <kbd>{{ t('em.removeKey') }}</kbd>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { t } from '@vem/i18n.js';

let seq = 0;
const W = 300;
const MARGIN = 8;
const GAP = 12;

const props = defineProps({
  /** Ancla (el asa de la línea), en px relativos a .canvas-box. */
  x: { type: Number, required: true },
  y: { type: Number, required: true },
  /** Tamaño de .canvas-box: el menú se recoloca para no salirse. */
  bounds: { type: Object, required: true },
  alias: { type: String, required: true },
  /** Etiqueta de la pregunta (aliasLabel o sysShort). */
  label: { type: String, required: true },
  /** Nombre del campo destino. */
  field: { type: String, required: true },
});
const emit = defineEmits(['close', 'remove']);

const titleId = `em-title-${++seq}`;
const rootRef = ref(null);
const titleRef = ref(null);
const h = ref(0);

/** Debajo del ancla si cabe; si no, encima; siempre dentro de bounds. */
const boxStyle = computed(() => {
  const bw = props.bounds.w || W;
  const bh = props.bounds.h || 0;
  const w = Math.min(W, Math.max(0, bw - 2 * MARGIN));
  const left = Math.max(MARGIN, Math.min(props.x - w / 2, bw - w - MARGIN));
  let top = props.y + GAP;
  if (bh && top + h.value > bh - MARGIN) top = props.y - GAP - h.value;
  if (bh) top = Math.max(MARGIN, Math.min(top, bh - h.value - MARGIN));
  return { left: `${Math.round(left)}px`, top: `${Math.round(Math.max(MARGIN, top))}px`, width: `${w}px` };
});

function onKey(e) {
  if (e.key === 'Escape') emit('close');
  else if (e.key === 'Delete') emit('remove');
  else return;
  e.preventDefault();
  e.stopPropagation();
}

function onDocDown(e) {
  if (rootRef.value && !rootRef.value.contains(e.target)) emit('close');
}

onMounted(() => {
  nextTick(() => {
    if (rootRef.value) h.value = rootRef.value.offsetHeight;
    titleRef.value?.focus({ preventScroll: true });
  });
  document.addEventListener('pointerdown', onDocDown);
});
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocDown));
</script>

<style>
.vem-root .em {
  position: absolute; z-index: 25; background: var(--panel); border: 1px solid var(--line2);
  border-top: 3px solid var(--teal); border-radius: 12px; padding: 10px 12px 10px; box-shadow: 0 14px 36px var(--shadow);
  font-size: 12.5px;
}
.vem-root .em-head { display: flex; align-items: flex-start; gap: 8px; }
.vem-root .em-title { flex: 1; min-width: 0; margin: 0; font-size: 13.5px; font-weight: 600; line-height: 1.35; word-break: normal; overflow-wrap: anywhere; }
.vem-root .em-title:focus { outline: none; }
.vem-root .em-title:focus-visible { outline: 2px solid var(--mint); outline-offset: 2px; border-radius: 4px; }
.vem-root .em-arrow { color: var(--teal); }
.vem-root .em-title .mono { color: var(--lime); font-size: 12.5px; }
.vem-root .em-x { background: none; border: 0; color: var(--tx2); cursor: pointer; padding: 0 2px; font-size: 14px; line-height: 1; }
.vem-root .em-sub { margin-top: 3px; font-size: 10.5px; color: var(--tx3); overflow-wrap: anywhere; }
.vem-root .em-act {
  display: flex; align-items: center; gap: 6px; background: none; border: 0; border-radius: 7px; padding: 5px 6px;
  cursor: pointer; text-align: left; font-size: 12.5px;
}
.vem-root .em-act > span[aria-hidden] { flex: 0 0 14px; text-align: center; }
.vem-root .em-act:hover { background: var(--hover); }
.vem-root .em-act.danger, .vem-root .em-act.danger > span[aria-hidden] { color: var(--danger); }
.vem-root .em-act kbd { font-family: var(--mono); font-size: 10px; color: var(--tx3); border: 1px solid var(--line2); border-radius: 4px; padding: 0 4px; }
.vem-root .em-foot { display: flex; align-items: center; border-top: 1px solid var(--line2); margin: 8px -6px 0; padding-top: 6px; }
</style>
